import * as yup from 'yup';
import { WORKSPACE_CONTACT_FIELDS } from '../../../components/WorkspaceDetails/ContactDetails/contactDetailsConfig';
import { AddressCityField } from '../fields/addressCity/AddressCityField';
import { AddressCountryField } from '../fields/addressCountry/AddressCountryField';
import { AddressLineField } from '../fields/addressLine/AddressLineField';
import { AddressPostcodeField } from '../fields/addressPostcode/AddressPostcodeField';
import { AddressStateField } from '../fields/addressState/AddressStateField';
import { BusinessNumberField } from '../fields/businessNumber/BusinessNumberField';
import { BusinessNumberCountryField } from '../fields/businessNumberCountry/BusinessNumberCountryField';
import { canProceedSchema } from '../fields/canProceed/canProceedSchema';
import { CategoriesField } from '../fields/categories/CategoriesField';
import { ContactNumberField } from '../fields/contactNumber/ContactNumberField';
import { EntityNameField } from '../fields/entityName/EntityNameField';
import { EntityTypeField } from '../fields/entityType/EntityTypeField';
import { RepresentsClientsField } from '../fields/representsClients/RepresentsClientsField';
import { TaxResidencyField } from '../fields/taxResidency/TaxResidencyField';
import { WebsiteField } from '../fields/website/WebsiteField';
import { WorkspaceNameField } from '../fields/workspaceName/WorkspaceNameField';
import { ADDRESS_GROUP_EDGES } from './addressGroup';
import { getAddressFieldLabels } from './addressLabels';
import { requiredNonBlankString } from './countryAwareStringSchema';
import { WORKSPACE_DETAILS_FORM_FIELDS } from './fieldNames';

/**
 * Contact role assignments are all optional selects — no role is required,
 * matching the legacy contact-details behaviour. Blanks are stripped from the
 * payload by the submit mapper.
 *
 * Derived from WORKSPACE_CONTACT_FIELDS so a new role added there is
 * automatically validated instead of silently stripped by yup.
 */
const contactsSchema = yup.object(
  Object.fromEntries(
    WORKSPACE_CONTACT_FIELDS.map((field) => [field.name, yup.string()]),
  ),
);

/**
 * Composed Entity Details schema. Field schemas are registered here only — no
 * inline schema lives in the section container (modular-form contract).
 *
 * The address group fields are mutually `when()`-dependent (required only
 * while the group has content), so their edges are excluded from yup's
 * dependency sort via ADDRESS_GROUP_EDGES.
 */
export const workspaceDetailsEntitySchema = yup.object().shape(
  {
    [WORKSPACE_DETAILS_FORM_FIELDS.addressCity]: AddressCityField.schema,
    [WORKSPACE_DETAILS_FORM_FIELDS.addressCountry]: AddressCountryField.schema,
    [WORKSPACE_DETAILS_FORM_FIELDS.addressLine1]: AddressLineField.schema,
    [WORKSPACE_DETAILS_FORM_FIELDS.addressPostcode]:
      AddressPostcodeField.schema,
    [WORKSPACE_DETAILS_FORM_FIELDS.addressState]: AddressStateField.schema,
    [WORKSPACE_DETAILS_FORM_FIELDS.businessNumber]: BusinessNumberField.schema,
    [WORKSPACE_DETAILS_FORM_FIELDS.businessNumberCountry]:
      BusinessNumberCountryField.schema,
    [WORKSPACE_DETAILS_FORM_FIELDS.canProceed]: canProceedSchema,
    [WORKSPACE_DETAILS_FORM_FIELDS.categories]: CategoriesField.schema,
    [WORKSPACE_DETAILS_FORM_FIELDS.contacts]: contactsSchema,
    [WORKSPACE_DETAILS_FORM_FIELDS.entityName]: EntityNameField.schema,
    [WORKSPACE_DETAILS_FORM_FIELDS.entityType]: EntityTypeField.schema,
    [WORKSPACE_DETAILS_FORM_FIELDS.phone]: ContactNumberField.schema,
    [WORKSPACE_DETAILS_FORM_FIELDS.representsClients]:
      RepresentsClientsField.schema,
    [WORKSPACE_DETAILS_FORM_FIELDS.taxResidency]: TaxResidencyField.schema,
    [WORKSPACE_DETAILS_FORM_FIELDS.website]: WebsiteField.schema,
    [WORKSPACE_DETAILS_FORM_FIELDS.workspaceName]: WorkspaceNameField.schema,
  },
  ADDRESS_GROUP_EDGES,
);

/**
 * Mirrors the API's `ENTITY_DETAILS_LOCKED_FIELDS` (entity-details-lock.ts),
 * minus the derived values with no schema entry, plus `canProceed` — the
 * ABN-lookup gate, unreachable once `businessNumber` is locked.
 */
const ENTITY_DETAILS_VALIDATED_FIELDS = [
  WORKSPACE_DETAILS_FORM_FIELDS.businessNumber,
  WORKSPACE_DETAILS_FORM_FIELDS.businessNumberCountry,
  WORKSPACE_DETAILS_FORM_FIELDS.canProceed,
  WORKSPACE_DETAILS_FORM_FIELDS.categories,
  WORKSPACE_DETAILS_FORM_FIELDS.entityName,
  WORKSPACE_DETAILS_FORM_FIELDS.entityType,
  WORKSPACE_DETAILS_FORM_FIELDS.representsClients,
  WORKSPACE_DETAILS_FORM_FIELDS.taxResidency,
  WORKSPACE_DETAILS_FORM_FIELDS.workspaceName,
] as const;

/**
 * Variant for a membership in review (APP-2254). A disabled `<fieldset>` stops
 * the member typing, but react-hook-form validates its own values regardless —
 * so a required locked field they cannot reach failed every submit, turning
 * "entity details are read-only" into "the whole page is read-only".
 *
 * Safe to drop from validation, not from the payload: the mapper still echoes
 * the stored values and the API refuses any that differ.
 *
 * The legacy form has the same problem and the same fix in
 * `workspaceLockedSchema` (schema/workspaceSchema.ts); keep the two in step.
 */
export const workspaceDetailsLockedSchema = workspaceDetailsEntitySchema.omit(
  ENTITY_DETAILS_VALIDATED_FIELDS,
);

/**
 * Onboarding variant. A workspace category is mandatory to complete
 * onboarding but optional in settings — legacy parity with the
 * `categories-validator` in schema/workspaceSchema.ts, which only enforced
 * the rule when `context.isOnboarding` was set.
 *
 * Address requiredness needs no override: onboarding seeds `addressCountry`
 * (see workspaceToEntityFormDefaults), which arms the all-or-nothing address
 * group, and the country combobox sets `allowDeselect: false` so it cannot be
 * un-armed.
 */
export const workspaceDetailsOnboardingSchema =
  workspaceDetailsEntitySchema.shape({
    [WORKSPACE_DETAILS_FORM_FIELDS.categories]: CategoriesField.schema.min(
      1,
      'Please select a Category',
    ),
  });

const requiredAddressField = (labelKey: 'city' | 'postcode' | 'state') =>
  yup
    .string()
    .when(
      WORKSPACE_DETAILS_FORM_FIELDS.addressCountry,
      ([country], schema: yup.StringSchema) =>
        requiredNonBlankString(
          getAddressFieldLabels(country)[labelKey],
          schema,
        ),
    );

/**
 * Applying for membership (APP-2386). Settings lets a fully blank address
 * through so legacy workspaces can still save; an application needs one.
 */
export const workspaceDetailsMembershipSchema =
  workspaceDetailsEntitySchema.shape({
    [WORKSPACE_DETAILS_FORM_FIELDS.addressCity]: requiredAddressField('city'),
    [WORKSPACE_DETAILS_FORM_FIELDS.addressCountry]: yup
      .string()
      .required('Please select a Country'),
    [WORKSPACE_DETAILS_FORM_FIELDS.addressLine1]:
      requiredNonBlankString('Address'),
    [WORKSPACE_DETAILS_FORM_FIELDS.addressPostcode]:
      requiredAddressField('postcode'),
    [WORKSPACE_DETAILS_FORM_FIELDS.addressState]: requiredAddressField('state'),
  });
